import { useEffect, useState } from 'react';

import { type KeyPressCallback, useKeyPress } from '@pkg/hooks';
import { filterLineBreaks } from '@pkg/utils';

import { type TextAreaChange, type TextAreaFocus, type TextAreaHookOptions } from './TextArea.types';

export type TextAreaHookReturn = ReturnType<typeof useTextArea>;

const MAX_LENGTH = 9999;

function validate(value = '', { min = 0, max = MAX_LENGTH }) {
    // TODO: Eventually, this should validate against an allowed `pattern`,
    // such as restricting certain special characters.
    return value.length >= min && value.length <= max;
}

// NOTES:
// 1. Will NOT re-render if `initialValue` changes.
//  - This is by design (treat this like the React `input` API).
// 2. Currently not updating `value` when `min/max` changes.
//  - We can achieve this via a `useEffect` if we find the need.
export function useTextArea({
    initialValue = '',
    placeholder = '',
    minLength = 0,
    maxLength = MAX_LENGTH,
    disabled = false,
    readOnly = false,
    filtered = false,
    onBlur,
    onFocus,
    onEnter,
    onEscape,
    onChange,
}: TextAreaHookOptions) {
    const [focused, setFocused] = useState(false);
    const [liveValue, setLiveValue] = useState(`${initialValue}`);
    const [queuedValue, setQueuedValue] = useState('');
    const [savedValue, setSavedValue] = useState(`${initialValue}`);

    // Always including a trailing space after the `value`.
    // Otherwise, new lines will not compute a new height
    // until a text character is entered.
    const mirrorValue = liveValue.length ? `${liveValue} ` : placeholder;
    const disabledKeyPress = disabled || readOnly || !focused;

    const handleEnter: KeyPressCallback = (event) => {
        const parsedValue = filtered ? liveValue.trim() : liveValue;

        if (filtered) {
            event.preventDefault();
            setLiveValue(parsedValue);
            setQueuedValue(parsedValue);
        }

        onEnter?.(parsedValue);
    };

    const handleEscape: KeyPressCallback = (_event) => {
        const revertedValue = queuedValue ? queuedValue : savedValue;

        setLiveValue(revertedValue);
        onEscape?.(revertedValue, queuedValue);
    };

    // We are choosing not to `blur` within the hook,
    // and will instead leave that up to the consumer.
    useKeyPress(['Escape'], handleEscape, { disabled: disabledKeyPress });
    useKeyPress(['Enter'], handleEnter, { disabled: disabledKeyPress });

    const handleBlur: TextAreaFocus = ({ target }) => {
        const targetValue = target.value;
        const parsedValue = filtered ? targetValue.trim() : targetValue;

        setFocused(false);
        setLiveValue(parsedValue);
        if (parsedValue !== savedValue) setQueuedValue(parsedValue);

        onBlur?.(parsedValue);
    };

    const handleFocus: TextAreaFocus = ({ target }) => {
        setFocused(true);

        if (!onFocus) return;

        const targetValue = target.value;
        const parsedValue = filtered ? targetValue.trim() : targetValue;

        onFocus(parsedValue);
    };

    const handleChange: TextAreaChange = ({ target }) => {
        const targetValue = target.value;
        const parsedValue = filtered ? filterLineBreaks(targetValue) : targetValue;

        const valid = validate(parsedValue, { min: minLength, max: maxLength });
        const validValue = valid ? parsedValue : parsedValue.slice(0, maxLength);

        setLiveValue(parsedValue);
        onChange?.(parsedValue, valid, validValue);
    };

    function handleReset() {
        setLiveValue(savedValue);
        setQueuedValue('');
    }

    function handleSubmit() {
        setQueuedValue('');
        setSavedValue(liveValue);
    }

    useEffect(() => {
        if (filtered) {
            setLiveValue(filterLineBreaks(liveValue).trim());
            setQueuedValue(filterLineBreaks(queuedValue).trim());
            setSavedValue(filterLineBreaks(savedValue).trim());
        }

        // We intentionally do not want to be calling this hook
        // every time `liveValue` changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtered]);

    return {
        value: liveValue,
        mirrorValue,
        focused,
        handleBlur,
        handleFocus,
        handleChange,
        handleReset,
        handleSubmit,
    };
}
